import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
// tslint:disable-next-line:import-blacklist
import { UntypedFormControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';
import { ApBaseComponent, ApNotificationService } from 'webcommon/core-ui';
import { FindControlIdService, WebCommonStorageRepository } from 'webcommon/legacy-common';
import { FindControlRequiredSearchParametersDto, ISelected, MacroDefinitionDto, MacroGroupDto } from 'webcommon/shared';
import { MacroService } from '../ap-macro/index';
import { MacroInterfaces } from '../ap-macro/MacroInterfaces';
import { ApMacroEditorModalService } from './ap-macro-editor-modal.service';

@Component({
  preserveWhitespaces: true,
  selector: 'ap-text-editor-macro-selector',
  templateUrl: './ap-text-editor-macro-selector.component.html',
})

export class ApTextEditorMacroSelectorComponent extends ApBaseComponent implements OnInit, OnDestroy {

  constructor(
    public apMacroEditorModalService: ApMacroEditorModalService,
    public findControlIdService: FindControlIdService,
    private webCommonStorageRepository: WebCommonStorageRepository,
    private apNotificationService: ApNotificationService,
  ) {
    super();
  }

  @Input() baseId: string;
  @Input() macroInterfaces: MacroInterfaces;
  @Input() newHidden: boolean;
  @Input() insertHidden: boolean;
  @Input() modifyHidden: boolean;
  @Input() modifyAlwaysHidden: boolean;
  @Input() macroService: MacroService;
  @Input() isDisabled = false;

  @Output() insertClicked: EventEmitter<MacroDefinitionDto> = new EventEmitter();

  private readonly waitTime = 200;

  filteredCount = 1;
  filteredMacros$: Observable<any>;
  findControlSearchParams: FindControlRequiredSearchParametersDto =
    {
      RequiredSearchParameters:
        [
          {
            ColumnName: 'UserUid',
            Value: this.getDefaultUserId(),
          },
        ],
    };
  groups: string[] = [];
  hidden = true;
  Id: string;
  macros: MacroDefinitionDto[] = [];
  saving = false;
  selectedMacro: MacroDefinitionDto | undefined;
  selectedMacroDescriptionText: string | null = null;

  private macroNameFilterSubject = new BehaviorSubject<string>('');
  macroNameFilter$ = this.macroNameFilterSubject.asObservable();
  macroNameFilterFormControl: UntypedFormControl = new UntypedFormControl('');

  private macroGroupSubject = new BehaviorSubject<string[]>([]);
  macroGroupFilter$ = this.macroGroupSubject.asObservable();

  private macroSubject = new BehaviorSubject<MacroDefinitionDto[]>([]);
  macros$ = this.macroSubject.asObservable();

  ngOnInit() {
    this.setId();
    this.loadMacros();
    this.subscribeToMacroListChanges();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  onMacroSelected(selectedMacro: MacroDefinitionDto) {
    if (this.selectedMacro) {
      (this.selectedMacro as ISelected).Selected = false;
    }

    (selectedMacro as ISelected).Selected = !(selectedMacro as ISelected).Selected;
    this.hidden = !(selectedMacro as ISelected).Selected;
    this.selectedMacro = !(selectedMacro as ISelected).Selected ? undefined : selectedMacro;
    this.selectedMacroDescriptionText = null;

    if (this.selectedMacro) {
      this.modifyHidden = !this.selectedMacro.IsUserDefined;
      this.selectedMacroDescriptionText = 'Macro Group: ' + this.selectedMacro.MacroGroup.Name;
    }
  }

  onInsert(insertBtn: HTMLButtonElement) {
    // Deselects the button after click
    insertBtn.blur();

    if (this.selectedMacro) {
      this.insertClicked.emit(this.selectedMacro);
    }
  }

  onMacroGroupSelected(macroGroups: string[]) {
    this.macroGroupSubject.next(macroGroups);
  }

  onModify() {
    if (!this.selectedMacro) return;
    this.apMacroEditorModalService.openModifyModal(this.selectedMacro, this.Id, this.macroService, this.macroInterfaces)
      .then((response) => {
        this.saving = response;
        // this.getMacroList();
      },
        () => {
          // Do nothing
        });
  }

  onDelete() {
    if (!this.selectedMacro || !this.selectedMacro.Id) return;
    const macroToDelete = JSON.parse(JSON.stringify(this.selectedMacro)) as MacroDefinitionDto;

    this.macroService.deleteMacro(this.selectedMacro.Id, this.macroInterfaces.closeObs()).pipe(
      this.takeUntilDestroy(),
    ).subscribe(
      (data) => {
        if (data.Deleted.length > 0) {
          this.apNotificationService.successWList(data.Deleted);
        }
        if (data.Errors.length > 0) {
          this.apNotificationService.errorWList(data.Errors);
        }
      },
      (error) => {
        const msg = `Unable to delete ${macroToDelete.Name} macro`;
        this.apNotificationService.error(msg);
        throw (error);
      },
    );
  }

  onClone() {
    if (!this.selectedMacro) return;
    this.apMacroEditorModalService.openCloneModal(this.selectedMacro, this.Id, this.macroService, this.macroInterfaces)
      .then((data: boolean) => {
        this.saving = data;
      },
        () => { /*user cancelled so nothing to do*/ },
      );
  }

  onNew() {
    let defaultMacroGroup: MacroGroupDto | null = null;
    if (this.groups && this.groups.length === 1) {
      defaultMacroGroup = { MacroGroupId: this.groups[0], Name: '' };
    }
    this.apMacroEditorModalService.openCreateModal(this.Id, this.macroService, this.macroInterfaces, defaultMacroGroup)
      .then((data: boolean) => {
        this.saving = data;
      },
        () => { /*user cancelled so nothing to do*/ },
      );
  }

  private loadMacros() {
    if (this.macros && this.macros.length > 0) return;
    this.getMacroList();
    this.loadFilters();
  }

  private loadFilters() {
    this.macroNameFilterSetup();
    this.macroGroupSubject.next([]);
    // First filter by Macro Group if any are selected
    // then filter by Macro Name
    this.filteredMacros$ = combineLatest([this.macros$, this.macroGroupFilter$, this.macroNameFilter$]).pipe(
      map(([macros, macroGroupFilter, filter]): [MacroDefinitionDto[], string] => {
        return [this.filterByMacroGroup(macros, macroGroupFilter), filter];
      }),
      map(([macros, filterString]) => this.filterByMacroName(macros, filterString)),
    );
  }

  private filterByMacroGroup(macros: MacroDefinitionDto[], macroGroupFilter: string[]): MacroDefinitionDto[] {
    return macros.filter((macro) =>
      !macroGroupFilter || macroGroupFilter.length === 0
      || macroGroupFilter.includes(macro.MacroGroup.MacroGroupId));
  }

  private filterByMacroName(macros: MacroDefinitionDto[], filter: string) {
    this.hidden = true;
    this.selectedMacro = undefined;
    const filtered = macros.filter((macro) => {
      if (!macro.Name) {
        return false;
      }
      return macro.Name.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
    });
    if (filtered.length === 0) {
      return null;
    }
    return filtered;
  }

  private macroNameFilterSetup() {
    this.macroNameFilterFormControl.valueChanges.pipe(
      debounceTime(this.waitTime),
      takeUntil(this.onDestroy$),
    ).subscribe((searchString: string) => {
      this.macroNameFilterSubject.next(searchString);
    });
  }

  private getMacroList() {
    this.macroSubject.next(this.macroService.getMatchingResults('', this.macroInterfaces));
  }

  private getDefaultUserId() {
    let userId = this.webCommonStorageRepository.getUserId();

    if (!userId) {
      userId = '';
    }
    return userId;
  }

  private setId() {
    this.Id = `${this.baseId}-macro-selector`;
  }
  private subscribeToMacroListChanges() {
    this.macroService.macroListChanged$
      .pipe(
        debounceTime(300),
        this.takeUntilDestroy(),
      )
      .subscribe(
        (data) => {
          if (data) {
            this.getMacroList();
          }
        },
        () => { /* do nothing */ },
      );
  }
}
